import {
  ApplicationBusinessUnitSchema,
  BusinessUnitSchema,
  BusinessUnitWithRelationsSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

// Error response schema
export const ErrorResponseSchema = z.object({
  message: z.string(),
});

// Create custom schemas for API operations since Prisma schemas are complex
export const CreateBusinessUnitSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  color: z.string().optional(),
});

export const UpdateBusinessUnitSchema = z
  .object({
    name: z.string().min(1, 'Name is required').optional(),
    description: z.string().optional(),
    color: z.string().optional(),
  })
  .partial();

// Re-export the Prisma-generated schemas
export { BusinessUnitSchema, BusinessUnitWithRelationsSchema };

// Custom schemas for API responses
export const BusinessUnitWithApplicationsSchema = BusinessUnitSchema.extend({
  applications: z.array(
    z.object({
      id: z.string(),
      application: z.object({
        id: z.string(),
        name: z.string(),
        url: z.string(),
        environment: z.string(),
      }),
    }),
  ),
});

export const UpdateApplicationBusinessUnitsSchema = z.object({
  businessUnitIds: z.array(z.string()),
});

export const ApplicationBusinessUnitWithDetailsSchema =
  ApplicationBusinessUnitSchema.extend({
    businessUnit: BusinessUnitSchema,
  });

// Dashboard Metrics Schemas
export const BusinessUnitFindingsBySeveritySchema = z.object({
  critical: z.number(),
  high: z.number(),
  medium: z.number(),
  low: z.number(),
  info: z.number(),
});

export const BusinessUnitPerformanceMetricsSchema = z.object({
  businessUnit: z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable().optional(),
  }),
  applicationsCount: z.number(),
  findingsCount: z.number(),
  findingsBySeverity: BusinessUnitFindingsBySeveritySchema,
  riskScore: z.number(),
});

export const BusinessUnitDashboardMetricsSchema = z.object({
  performance: BusinessUnitPerformanceMetricsSchema.array(),
  totalBusinessUnits: z.number(),
  totalApplications: z.number(),
  totalFindings: z.number(),
  averageRiskScore: z.number(),
});

export const BusinessUnitSummaryMetricsSchema = z.object({
  businessUnitId: z.string(),
  businessUnitName: z.string(),
  applicationsCount: z.number(),
  findingsCount: z.number(),
  criticalFindings: z.number(),
  highFindings: z.number(),
  riskScore: z.number(),
  lastUpdated: z.iso.datetime(),
});

// Additional Dashboard Widget Schemas
export const BusinessUnitRiskDistributionSchema = z.object({
  lowRisk: z.number(), // 0-25
  mediumRisk: z.number(), // 26-50
  highRisk: z.number(), // 51-75
  criticalRisk: z.number(), // 76-100
});

export const TopRiskyBusinessUnitSchema = z.object({
  businessUnit: z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable().optional(),
  }),
  riskScore: z.number(),
  criticalFindings: z.number(),
  highFindings: z.number(),
  applicationsCount: z.number(),
  trend: z.enum(['increasing', 'decreasing', 'stable']).optional(),
});

export const BusinessUnitRemediationProgressSchema = z.object({
  businessUnit: z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable().optional(),
  }),
  totalFindings: z.number(),
  resolvedFindings: z.number(),
  inProgressFindings: z.number(),
  unresolvedFindings: z.number(),
  remediationRate: z.number(), // percentage
  averageTimeToResolve: z.number(), // days
});

export const BusinessUnitComplianceScoreSchema = z.object({
  businessUnit: z.object({
    id: z.string(),
    name: z.string(),
    color: z.string().nullable().optional(),
  }),
  complianceScore: z.number(), // 0-100
  passedChecks: z.number(),
  totalChecks: z.number(),
  lastAssessment: z.iso.datetime(),
  trend: z.enum(['improving', 'declining', 'stable']).optional(),
});

// Export types
export type BusinessUnitFindingsBySeverity = z.infer<
  typeof BusinessUnitFindingsBySeveritySchema
>;
export type BusinessUnitPerformanceMetrics = z.infer<
  typeof BusinessUnitPerformanceMetricsSchema
>;
export type BusinessUnitDashboardMetrics = z.infer<
  typeof BusinessUnitDashboardMetricsSchema
>;
export type BusinessUnitSummaryMetrics = z.infer<
  typeof BusinessUnitSummaryMetricsSchema
>;

// Export types for additional widgets
export type BusinessUnitRiskDistribution = z.infer<
  typeof BusinessUnitRiskDistributionSchema
>;
export type TopRiskyBusinessUnit = z.infer<typeof TopRiskyBusinessUnitSchema>;
export type BusinessUnitRemediationProgress = z.infer<
  typeof BusinessUnitRemediationProgressSchema
>;
export type BusinessUnitComplianceScore = z.infer<
  typeof BusinessUnitComplianceScoreSchema
>;
